import { getDataValue, setDataValue } from '@juvare/json-data-util';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import { getErrorMessage } from '@juvare/widget-ui/Utils';
import PasswordIcon from '@mui/icons-material/Password';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { CircularProgress, FormControl, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormPassword = (props) => {
    var _a, _b, _c, _d;
    const value = props.getDataString();
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const getInputType = () => {
        if (props.config.inputType == null || props.config.inputType === 'password') {
            return props.internalData.showPassword ? 'text' : 'password';
        }
        return 'text';
    };
    const getWebkitTextSecurityProp = () => {
        if (props.config.inputType === 'mask') {
            return !props.internalData.showPassword ? 'disc' : 'none';
        }
        return 'none';
    };
    const handleShowPassword = () => {
        props.internalData.showPassword = !props.internalData.showPassword;
        props.update();
        renderForm(props);
    };
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error, disabled: props.config.disabled },
        React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error, disabled: props.config.disabled }, elementLabel),
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(OutlinedInput, { id: props.id, fullWidth: true, label: elementLabel, type: getInputType(), required: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.required, error: !!error, value: value, readOnly: props.internalData.loading, onChange: (event) => {
                    let newValue = event.target.value;
                    if (props.config.emptyAsNull && !newValue) {
                        newValue = undefined;
                    }
                    props.setDataStringOrUndefined(newValue);
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, disabled: props.config.disabled, inputProps: {
                    sx: {
                        WebkitTextSecurity: getWebkitTextSecurityProp(),
                    },
                    tabIndex: props.editMode ? -1 : undefined,
                    inputMode: props.config.inputMode,
                    'aria-describedby': ariaDescribedBy,
                }, autoFocus: (_d = (!props.editMode && props.config.autoFocus)) !== null && _d !== void 0 ? _d : false, autoComplete: props.config.inputType === 'mask' ? 'off' : undefined, endAdornment: !props.config.disabled &&
                    (props.internalData.loading ? (React.createElement(CircularProgress, { size: 24 })) : (React.createElement(InputAdornment, { position: 'end' },
                        React.createElement(Tooltip, { title: props.internalData.showPassword ? 'Hide' : 'Show' },
                            React.createElement(IconButton, { "aria-label": 'Toggle ' + props.config.label + ' visibility', "aria-description": props.internalData.showPassword ? 'Hide' : 'Show', onClick: handleShowPassword, edge: 'end' }, props.internalData.showPassword ? React.createElement(VisibilityOffOutlinedIcon, null) : React.createElement(VisibilityOutlinedIcon, null)))))) })),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormPasswordEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => {
            var _a;
            const passwordDecryptFunctions = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.passwordDecryptFunctions;
            const passwordFunctionValues = Object.entries(passwordDecryptFunctions !== null && passwordDecryptFunctions !== void 0 ? passwordDecryptFunctions : {}).map(([functionName, functionDef]) => ({
                value: functionName,
                label: functionDef.label,
                description: functionDef.description,
            }));
            const inputTypes = [
                {
                    value: 'password',
                    label: 'Password Support',
                    description: 'Allows your browser to generate, save, and enter passwords according to your browser settings.',
                },
                {
                    value: 'mask',
                    label: 'Data Masking',
                    description: 'Allows your browser to mask sensitive information, such as your SSN, making it not readable when you entering that data in the system.',
                },
            ];
            const inputModes = [
                {
                    value: 'text',
                    label: 'Text',
                    description: "Standard input keyboard for the user's current locale.",
                },
                {
                    value: 'numeric',
                    label: 'Numeric',
                    description: 'Numeric input keyboard with digits 0–9. Devices may or may not show a minus key.',
                },
            ];
            showWidgetEditDialog(props, endEditing, { title: 'Password: ' + props.config.label }, buildGeneralTab(props, {
                key: true,
                label: true,
                appendRowIndexToLabel: true,
                description: true,
                tooltip: true,
                autoFocus: true,
                disabled: true,
                children: [
                    {
                        type: 'form.advancedRadio',
                        key: 'inputType',
                        label: 'Input Field Type',
                        values: inputTypes,
                    },
                    {
                        type: 'form.advancedRadio',
                        key: 'inputMode',
                        label: 'Input Mode',
                        values: inputModes,
                    },
                    ...(passwordFunctionValues.length
                        ? [
                            {
                                type: 'form.select',
                                key: 'decryptFunctionName',
                                label: 'Decrypt Password When Form Loads',
                                showDescription: true,
                                values: passwordFunctionValues,
                            },
                        ]
                        : []),
                ],
                onLoad: (data) => {
                    if (!data.inputType) {
                        data.inputType = 'password';
                    }
                    if (!data.inputMode) {
                        data.inputMode = 'text';
                    }
                    return data;
                },
            }), buildValidateTab(props, {
                required: true,
                maxLength: true,
                minLength: true,
                uniqueInList: true,
                regex: true,
            }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true }));
        } }),
        React.createElement(WidgetFormPassword, Object.assign({}, props))));
};
export const widgetFormPassword = {
    type,
    load: (props) => {
        var _a;
        if (isSkipValidate(props)) {
            // Do not try to decrypt password here. This code path means that this widget is inside a modal,
            // but the modal itself is not visible. We want to decrypt only when widget is actually shown.
            // This will happen when modal is shown, then this flag will be false.
            return;
        }
        const passwordDecryptFunctions = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.passwordDecryptFunctions;
        if (props.config.decryptFunctionName) {
            const decryptedDataPath = props.dataPath + 'Decrypted';
            const alreadyDecrypted = !!getDataValue(props.rootData, decryptedDataPath);
            if (!alreadyDecrypted) {
                const decryptFunctionDef = passwordDecryptFunctions === null || passwordDecryptFunctions === void 0 ? void 0 : passwordDecryptFunctions[props.config.decryptFunctionName];
                if (decryptFunctionDef) {
                    props.internalData.loading = true;
                    props.update();
                    decryptFunctionDef
                        .decryptFunction(props.getDataString())
                        .then((decryptedValue) => {
                        delete props.internalData.loading;
                        let newValue = decryptedValue;
                        if (props.config.emptyAsNull && !newValue) {
                            newValue = undefined;
                        }
                        props.setDataStringOrUndefined(newValue);
                        setDataValue(props.rootData, props.dataPath + 'Decrypted', true);
                        renderForm(props);
                    })
                        .catch((error) => {
                        console.error('Failed to decrypt password using function ' + props.config.decryptFunctionName, error);
                        delete props.internalData.loading;
                        props.internalData.error = getErrorMessage(error);
                        props.update();
                        renderForm(props);
                    });
                }
                else {
                    props.internalData.error = 'Decrypt function ' + props.config.decryptFunctionName + ' is not defined';
                }
            }
        }
    },
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        if (isSkipValidate(props)) {
            return;
        }
        if (props.internalData.error != null) {
            return props.internalData.error;
        }
        const value = props.getDataStringOrUndefined();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!value) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if (value) {
            let regex = undefined;
            if (typeof ((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.regex) === 'string') {
                let regexString = (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.regex;
                if (!(regexString === null || regexString === void 0 ? void 0 : regexString.startsWith('^'))) {
                    regexString = '^' + regexString;
                }
                if (!(regexString === null || regexString === void 0 ? void 0 : regexString.endsWith('$'))) {
                    regexString = regexString + '$';
                }
                try {
                    regex = new RegExp(regexString);
                }
                catch (error) {
                    console.error(props.config.label + ' invalid validation regex', error);
                    return props.config.label + ' invalid validation regex';
                }
            }
            else if (((_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.regex) instanceof RegExp) {
                regex = (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.regex;
            }
            if (regex && !regex.test(value)) {
                return ((_h = (_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.regexMessage) !== null && _h !== void 0 ? _h : props.config.label + ' does not match the pattern ' + ((_k = (_j = props.config.validate) === null || _j === void 0 ? void 0 : _j.regex) !== null && _k !== void 0 ? _k : ''.toString()));
            }
            if (((_l = props.config.validate) === null || _l === void 0 ? void 0 : _l.maxLength) != null && value.length > ((_m = props.config.validate) === null || _m === void 0 ? void 0 : _m.maxLength)) {
                return ((_o = props.config.validate.maxLengthMessage) !== null && _o !== void 0 ? _o : (props.config.label ? props.config.label + ' length cannot be greater than ' : 'Length cannot be greater than ') +
                    props.config.validate.maxLength);
            }
            if (((_p = props.config.validate) === null || _p === void 0 ? void 0 : _p.minLength) != null && value.length < ((_q = props.config.validate) === null || _q === void 0 ? void 0 : _q.minLength)) {
                return ((_r = props.config.validate.minLengthMessage) !== null && _r !== void 0 ? _r : (props.config.label ? props.config.label + ' length cannot be less than ' : 'Length cannot be less than ') +
                    props.config.validate.minLength);
            }
        }
        if ((_s = props.config.validate) === null || _s === void 0 ? void 0 : _s.uniqueInList) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_t = props.config.validate.uniqueInListMessage) !== null && _t !== void 0 ? _t : error;
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), props.getDataStringOrUndefined());
    },
    component: WidgetFormPassword,
    editorComponent: WidgetFormPasswordEditor,
    icon: React.createElement(PasswordIcon, { fontSize: 'small' }),
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'password', label: 'Password' }),
};
